import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface GeneratedTask {
  title: string;
  description: string;
  estimated_hours: number;
  difficulty: "easy" | "medium" | "hard";
  skill_tags: string[];
  selected?: boolean;
}

interface AITaskGeneratorProps {
  challengeId: string;
  challengeTitle: string;
  problemStatement: string | null;
  onTasksGenerated: () => void;
}

const AITaskGenerator: React.FC<AITaskGeneratorProps> = ({
  challengeId,
  challengeTitle,
  problemStatement,
  onTasksGenerated,
}) => {
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatedTasks, setGeneratedTasks] = useState<GeneratedTask[]>([]);

  const generateTasks = async () => {
    if (!problemStatement) {
      toast.error("No problem statement available to generate tasks from");
      return;
    }

    setGenerating(true);
    setGeneratedTasks([]);

    try {
      const { data, error } = await supabase.functions.invoke(
        "generate-challenge-tasks",
        {
          body: {
            challengeTitle,
            problemStatement,
          },
        }
      );

      if (error) throw error;

      if (data?.tasks && Array.isArray(data.tasks)) {
        setGeneratedTasks(
          data.tasks.map((t: GeneratedTask) => ({ ...t, selected: true }))
        );
      } else {
        toast.error("Failed to parse generated tasks");
      }
    } catch (error) {
      console.error("Error generating tasks:", error);
      toast.error("Failed to generate tasks");
    } finally {
      setGenerating(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    if (generatedTasks.length === 0) {
      generateTasks();
    }
  };

  const toggleTask = (index: number) => {
    setGeneratedTasks((prev) =>
      prev.map((t, i) => (i === index ? { ...t, selected: !t.selected } : t))
    );
  };

  const selectAll = () => {
    setGeneratedTasks((prev) => prev.map((t) => ({ ...t, selected: true })));
  };

  const deselectAll = () => {
    setGeneratedTasks((prev) => prev.map((t) => ({ ...t, selected: false })));
  };

  const saveTasks = async () => {
    const selectedTasks = generatedTasks.filter((t) => t.selected);
    if (selectedTasks.length === 0) {
      toast.error("Please select at least one task");
      return;
    }

    setSaving(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const tasksToInsert = selectedTasks.map((t, i) => ({
        challenge_id: challengeId,
        title: t.title,
        description: t.description,
        estimated_hours: t.estimated_hours,
        difficulty: t.difficulty,
        skill_tags: t.skill_tags,
        order_index: i,
        created_by: user?.id,
      }));

      const { error } = await supabase
        .from("challenge_tasks")
        .insert(tasksToInsert);

      if (error) throw error;

      toast.success(`Added ${selectedTasks.length} tasks`);
      setOpen(false);
      setGeneratedTasks([]);
      onTasksGenerated();
    } catch (error) {
      console.error("Error saving tasks:", error);
      toast.error("Failed to save tasks");
    } finally {
      setSaving(false);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "easy":
        return "text-green-600";
      case "medium":
        return "text-yellow-600";
      case "hard":
        return "text-red-600";
      default:
        return "text-muted-foreground";
    }
  };

  return (
    <>
      <Button onClick={handleOpen} variant="outline" size="sm">
        <Sparkles className="h-4 w-4 mr-2" />
        AI Generate Tasks
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              AI Task Generator
            </DialogTitle>
            <DialogDescription>
              Select which tasks to add to this challenge
            </DialogDescription>
          </DialogHeader>

          {generating ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">
                Analyzing problem and generating tasks...
              </p>
            </div>
          ) : generatedTasks.length > 0 ? (
            <>
              <div className="flex gap-2 mb-2">
                <Button variant="ghost" size="sm" onClick={selectAll}>
                  Select All
                </Button>
                <Button variant="ghost" size="sm" onClick={deselectAll}>
                  Deselect All
                </Button>
                <Button variant="ghost" size="sm" onClick={generateTasks}>
                  <Sparkles className="h-4 w-4 mr-1" />
                  Regenerate
                </Button>
              </div>

              <ScrollArea className="flex-1 pr-4">
                <div className="space-y-3">
                  {generatedTasks.map((task, index) => (
                    <div
                      key={index}
                      className={`p-4 border rounded-lg transition-colors ${
                        task.selected
                          ? "border-primary bg-primary/5"
                          : "border-border"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={task.selected}
                          onCheckedChange={() => toggleTask(index)}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium">{task.title}</h4>
                            <span
                              className={`text-xs ${getDifficultyColor(
                                task.difficulty
                              )}`}
                            >
                              {task.difficulty}
                            </span>
                            {task.estimated_hours && (
                              <span className="text-xs text-muted-foreground">
                                ~{task.estimated_hours}h
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {task.description}
                          </p>
                          {task.skill_tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {task.skill_tags.map((tag) => (
                                <span
                                  key={tag}
                                  className="text-xs px-2 py-0.5 bg-secondary rounded-full"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p>No tasks generated yet</p>
              <Button onClick={generateTasks} className="mt-4">
                Generate Tasks
              </Button>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveTasks}
              disabled={
                saving || generatedTasks.filter((t) => t.selected).length === 0
              }
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                `Add ${generatedTasks.filter((t) => t.selected).length} Tasks`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AITaskGenerator;
